import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PlatformSidebar from "./components/PlatformSidebar";
import PlatformDashboard from "./components/PlatformDashboard";
import TenantManager from "./components/TenantManager";
import PlanManager from "./components/PlanManager";
import PlatformAnnouncement from "./components/PlatformAnnouncement";
import BillingRecords from "./components/BillingRecords";
import OperationLogs from "./components/OperationLogs";
import PlatformImageLibrary from "./components/PlatformImageLibrary";
import StorageAnalysis from "./components/StorageAnalysis";
import FeedbackCenter from "./components/FeedbackCenter";
import FanTopicsManager from "./components/FanTopicsManager";
import PlatformRegisteredUsers from "./components/PlatformRegisteredUsers";
import PlatformAbnormalOrders from "./components/PlatformAbnormalOrders";
import { platformAuth } from "./utils/platformAuth";
import { useTenants } from "../../api/useTenants";
import { fetchAbnormalOrderSummary } from "../../api/platformAbnormalOrders";
import { putPlatformAdminPassword } from "../../api/platformPassword";
import { getUserFacingMessage } from "../../api/errors";

type Section =
  | "dashboard"
  | "tenant-list"
  | "tenant-onboard"
  | "plan-manager"
  | "billing"
  | "announcements"
  | "operation-logs"
  | "platform-settings"
  | "image-library"
  | "storage-analysis"
  | "fan-interaction-topics"
  | "feedback-center"
  | "registered-users"
  | "abnormal-orders";

const sectionLabels: Record<Section, string> = {
  dashboard: "平台总览",
  "tenant-list": "租户列表",
  "tenant-onboard": "开通租户",
  "plan-manager": "套餐管理",
  billing: "收费记录",
  announcements: "平台公告",
  "registered-users": "注册用户",
  "operation-logs": "操作日志",
  "platform-settings": "平台设置",
  "image-library": "图片资源库",
  "storage-analysis": "存储空间分析",
  "fan-interaction-topics": "球迷话题",
  "feedback-center": "用户反馈",
  "abnormal-orders": "异常订单",
};

export default function PlatformPage() {
  const navigate = useNavigate();
  const { tenants } = useTenants();
  const [activeSection, setActiveSection] = useState<Section>("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [abnormalOrderCount, setAbnormalOrderCount] = useState(0);

  useEffect(() => {
    if (!platformAuth.isLoggedIn()) {
      navigate("/platform/login", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (!platformAuth.isLoggedIn()) return;
    void fetchAbnormalOrderSummary()
      .then((s) => setAbnormalOrderCount(s.count))
      .catch(() => setAbnormalOrderCount(0));
  }, [activeSection]);

  const handleLogout = () => {
    platformAuth.logout();
    navigate("/platform/login", { replace: true });
  };

  const pendingCount = tenants.filter((t) => t.status === "pending").length;

  const renderContent = () => {
    switch (activeSection) {
      case "dashboard":
        return <PlatformDashboard onNavigate={(s) => setActiveSection(s as Section)} />;
      case "tenant-list":
      case "tenant-onboard":
        return <TenantManager />;
      case "plan-manager":
        return <PlanManager />;
      case "billing":
        return <BillingRecords />;
      case "announcements":
        return <PlatformAnnouncement />;
      case "registered-users":
        return <PlatformRegisteredUsers />;
      case "operation-logs":
        return <OperationLogs />;
      case "image-library":
        return <PlatformImageLibrary />;
      case "storage-analysis":
        return <StorageAnalysis />;
      case "platform-settings":
        return <PlatformSettings />;
      case "fan-interaction-topics":
        return <FanTopicsManager />;
      case "feedback-center":
        return <FeedbackCenter />;
      case "abnormal-orders":
        return <PlatformAbnormalOrders onCountRefresh={setAbnormalOrderCount} />;
      default:
        return null;
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <div className={`${sidebarOpen ? "block" : "hidden"} lg:block flex-shrink-0`}>
        <PlatformSidebar
          activeSection={activeSection}
          onNavigate={(s) => setActiveSection(s as Section)}
          pendingCount={pendingCount}
          abnormalOrderCount={abnormalOrderCount}
          onLogout={handleLogout}
        />
      </div>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Topbar */}
        <header className="bg-white border-b border-gray-100 px-6 py-3.5 flex items-center gap-4 flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer rounded-lg hover:bg-gray-100 lg:hidden"
          >
            <i className="ri-menu-line"></i>
          </button>
          <div className="flex items-center gap-2 text-xs text-gray-400 flex-1">
            <span className="font-medium text-amber-600">FanHub 平台</span>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-600">{sectionLabels[activeSection]}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 hidden sm:block">平台运营端</span>
            <div className="flex items-center gap-1.5 bg-amber-50 text-amber-600 text-xs px-3 py-1.5 rounded-xl">
              <i className="ri-shield-star-line text-sm"></i>
              <span>超级管理员</span>
            </div>
            <a
              href="/admin"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-external-link-line text-sm"></i>
              访问租户后台
            </a>
          </div>
        </header>

        <main className="flex-1 p-6 overflow-y-auto">
          {renderContent()}
        </main>
      </div>
    </div>
  );
}

// Inline simple settings for platform
function PlatformSettings() {
  const [saved, setSaved] = useState(false);
  const [settings, setSettings] = useState({
    platformName: "FanHub",
    platformTagline: "球迷协会 SaaS 平台",
    supportEmail: "support@fanhub.com",
    maxTrialDays: 30,
    autoSuspendDays: 30,
    allowSelfSignup: false,
    requireApproval: true,
  });

  const [pwdOld, setPwdOld] = useState("");
  const [pwdNew, setPwdNew] = useState("");
  const [pwdConfirm, setPwdConfirm] = useState("");
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [pwdOk, setPwdOk] = useState<string | null>(null);

  const handleChangePlatformPassword = async () => {
    setPwdError(null);
    setPwdOk(null);
    const o = pwdOld.trim();
    const n = pwdNew.trim();
    const c = pwdConfirm.trim();
    if (!o || !n) {
      setPwdError("请填写当前密码和新密码。");
      return;
    }
    if (n.length < 6) {
      setPwdError("新密码至少 6 个字符。");
      return;
    }
    if (n !== c) {
      setPwdError("两次输入的新密码不一致。");
      return;
    }
    try {
      setPwdSaving(true);
      await putPlatformAdminPassword(o, n);
      setPwdOld("");
      setPwdNew("");
      setPwdConfirm("");
      setPwdOk("运营端登录密码已更新。当前会话仍有效，下次在登录页请使用新密码。");
    } catch (e) {
      setPwdError(getUserFacingMessage(e, "修改失败，请检查当前密码是否正确。"));
    } finally {
      setPwdSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-800">平台设置</h2>
          <p className="text-xs text-gray-400 mt-0.5">全局平台配置项</p>
        </div>
        <button
          type="button"
          onClick={() => { setSaved(true); setTimeout(() => setSaved(false), 2000); }}
          className={`flex items-center justify-center gap-2 px-4 py-2 text-sm rounded-xl cursor-pointer transition-all whitespace-nowrap ${
            saved ? "bg-green-500 text-white" : "bg-amber-500 text-white hover:bg-amber-400"
          }`}
        >
          <i className={saved ? "ri-check-line" : "ri-save-line"}></i>
          {saved ? "已保存" : "保存设置"}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl p-5 space-y-4">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <i className="ri-apps-2-line text-amber-500"></i> 平台基本信息
          </h3>
          {[
            { label: "平台名称", field: "platformName" as const },
            { label: "平台标语", field: "platformTagline" as const },
            { label: "支持邮箱", field: "supportEmail" as const },
          ].map((item) => (
            <div key={item.field}>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">{item.label}</label>
              <input
                type="text"
                value={String(settings[item.field])}
                onChange={(e) => setSettings((p) => ({ ...p, [item.field]: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400"
              />
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl p-5 space-y-4">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <i className="ri-settings-3-line text-amber-500"></i> 租户规则
          </h3>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">试用期天数</label>
            <input
              type="number"
              value={settings.maxTrialDays}
              onChange={(e) => setSettings((p) => ({ ...p, maxTrialDays: parseInt(e.target.value) || 0 }))}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">自动停用（到期后 N 天）</label>
            <input
              type="number"
              value={settings.autoSuspendDays}
              onChange={(e) => setSettings((p) => ({ ...p, autoSuspendDays: parseInt(e.target.value) || 0 }))}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400"
            />
          </div>
          <div className="space-y-3">
            {[
              { label: "允许租户自助注册", field: "allowSelfSignup" as const },
              { label: "新租户需人工审核", field: "requireApproval" as const },
            ].map((item) => (
              <div key={item.field} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <span className="text-xs text-gray-700">{item.label}</span>
                <button
                  onClick={() => setSettings((p) => ({ ...p, [item.field]: !p[item.field] }))}
                  className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${
                    settings[item.field] ? "bg-amber-500" : "bg-gray-300"
                  }`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${settings[item.field] ? "left-5" : "left-0.5"}`}></span>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-5 bg-white rounded-2xl p-5 space-y-4 border border-gray-100">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <i className="ri-shield-keyhole-line text-amber-500"></i>
          运营端登录安全
        </h3>
        <p className="text-xs text-gray-500 leading-relaxed">
          此处修改的是「平台运营端」登录页（<code className="text-gray-600">/platform/login</code>）的口令，需已登录运营端后操作；与各租户协会后台超级管理员密码无关。
        </p>
        {pwdError && (
          <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">{pwdError}</p>
        )}
        {pwdOk && (
          <p className="text-xs text-green-700 bg-green-50 border border-green-100 rounded-xl px-3 py-2">{pwdOk}</p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">当前密码</label>
            <input
              type="password"
              autoComplete="current-password"
              value={pwdOld}
              onChange={(e) => setPwdOld(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">新密码（至少 6 位）</label>
            <input
              type="password"
              autoComplete="new-password"
              value={pwdNew}
              onChange={(e) => setPwdNew(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">确认新密码</label>
            <input
              type="password"
              autoComplete="new-password"
              value={pwdConfirm}
              onChange={(e) => setPwdConfirm(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400"
            />
          </div>
        </div>
        <button
          type="button"
          disabled={pwdSaving}
          onClick={() => void handleChangePlatformPassword()}
          className="flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-gray-800 rounded-xl hover:bg-gray-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {pwdSaving ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              提交中…
            </>
          ) : (
            <>
              <i className="ri-lock-password-line"></i>
              修改运营端密码
            </>
          )}
        </button>
      </div>
    </div>
  );
}
